"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Facebook, Twitter, Instagram, Youtube, ArrowUp, Loader, Mail, Check, ChevronRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function SocialIcon({
  href,
  icon: Icon
}: {
  href: string;
  icon: React.ElementType;
}) {
  return <Link to={href} newTab className="text-muted-foreground hover:text-primary transition-colors duration-300">
      <Icon className="h-5 w-5" />
    </Link>;
}
function FooterHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="text-lg font-bold tracking-tight text-foreground mb-6 uppercase font-mono">
      {children}
    </h3>;
}
function FooterLink({
  to,
  children
}: {
  to: string;
  children: React.ReactNode;
}) {
  return <Link to={to} className="group flex items-center text-sm text-muted-foreground hover:text-primary transition-colors duration-300">
      <ChevronRight className="h-3 w-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300 text-primary" />
      {children}
    </Link>;
}
function ShopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-24 bg-muted/20 rounded animate-pulse" />)}
      </div>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <FooterLink to={item.href || "#"}>{item.label}</FooterLink>
        </li>)}
    </ul>;
}
function SupportMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-24 bg-muted/20 rounded animate-pulse" />)}
      </div>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <FooterLink to={item.href || "#"}>{item.label}</FooterLink>
        </li>)}
    </ul>;
}
function NewsletterFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 text-center animate-in fade-in zoom-in duration-500">
        <Check className="h-8 w-8 text-primary mx-auto mb-2" />
        <p className="text-primary font-medium">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="first_name" render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input placeholder="First Name" {...field} className="bg-muted/10 border-muted/20 focus-visible:border-primary focus-visible:ring-primary/20 h-11 text-foreground placeholder:text-muted-foreground" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^\S+@\S+$/i,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input type="email" placeholder="Email Address" {...field} className="bg-muted/10 border-muted/20 focus-visible:border-primary focus-visible:ring-primary/20 h-11 text-foreground placeholder:text-muted-foreground" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="agree_terms" rules={{
      required: "You must agree to receive emails"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 h-4 w-4 shrink-0 rounded-sm border border-muted-foreground/40 bg-transparent text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background accent-primary cursor-pointer" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-xs text-muted-foreground font-normal cursor-pointer">
                I agree to receive promotional emails. You can unsubscribe at any time.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <p className="text-destructive text-sm bg-destructive/10 p-2 rounded">{submitError}</p>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-bold tracking-wide uppercase">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Joining...
          </> : "Join the Inner Circle"}
      </Button>
    </Form>;
}
export default function TheStrideFooter() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  };
  return <section id="the-stride-footer" className="dark bg-background text-foreground pt-16 pb-8 border-t border-border/10 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-primary/50 blur-[100px] pointer-events-none" />

      <div ref={ref} className={`container mx-auto px-4 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        {}
        <div className="mb-16 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-3 tracking-tighter">
              <span className="text-foreground">UNLOCK EXCLUSIVE ACCESS</span>
            </h2>
            <p className="text-primary text-xl font-mono uppercase tracking-widest mb-2">
              Join the Inner Circle
            </p>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Be the first to know about limited drops, performance lab updates, and exclusive member-only offers.
            </p>
          </div>

          <div className="bg-card/30 backdrop-blur-sm border border-border/50 rounded-xl p-6 md:p-8 shadow-2xl shadow-black/20">
            <FormProvider formId="newsletter_signup_home" sectionName="footer_newsletter" defaultValues={{
            first_name: "",
            email: "",
            agree_terms: false
          }} validationRules={{
            first_name: {
              required: false
            },
            email: {
              required: true,
              pattern: "email"
            },
            agree_terms: {
              required: true
            }
          }} submitText="Join Now" successMessage="Welcome to the Inner Circle. Check your inbox.">
              <NewsletterFormContent />
            </FormProvider>
          </div>
        </div>

        <Separator className="bg-border/30 mb-16" />

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {}
          <div className="space-y-6">
            <Link to="/" className="block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/stridevault_logo.png" alt="StrideVault" className="h-7 w-auto" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              StrideVault is a modern ecommerce destination specializing in premium sports shoes for athletes and casual runners. Experience the future of footwear.
            </p>
            <div className="flex items-center gap-4">
              <SocialIcon href="https://facebook.com" icon={Facebook} />
              <SocialIcon href="https://twitter.com" icon={Twitter} />
              <SocialIcon href="https://instagram.com" icon={Instagram} />
              <SocialIcon href="https://youtube.com" icon={Youtube} />
            </div>
          </div>

          {}
          <div>
            <FooterHeading>Shop</FooterHeading>
            <MenuProvider menu_id="55" className="w-full">
              <ShopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div>
            <FooterHeading>Tech</FooterHeading>
            <ul className="space-y-3">
              <li>
                <FooterLink to="/performance-lab">Performance Lab</FooterLink>
              </li>
              <li>
                <FooterLink to="/shoe-tech-size-guide">Shoe Tech & Size Guide</FooterLink>
              </li>
              <li>
                <FooterLink to="/limited-drops">Limited Drops</FooterLink>
              </li>
              <li>
                <FooterLink to="/shoe-tech-size-guide#materials">Advanced Materials</FooterLink>
              </li>
            </ul>
          </div>

          {}
          <div>
            <FooterHeading>Support</FooterHeading>
            <MenuProvider menu_id="54" className="w-full">
              <SupportMenuConsumer />
            </MenuProvider>
            <div className="mt-6 pt-6 border-t border-border/20">
              <p className="text-xs text-muted-foreground mb-2">Need help?</p>
              <Link to="mailto:support@stridevault.com" className="flex items-center text-sm font-medium text-foreground hover:text-primary transition-colors">
                <Mail className="h-4 w-4 mr-2" />
                support@stridevault.com
              </Link>
            </div>
          </div>
        </div>

        <Separator className="bg-border/30 mb-8" />

        {}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>
            &copy; {new Date().getFullYear()} StrideVault. All rights reserved.
          </p>
          
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms of Service</Link>
            
            <Button variant="outline" size="icon" onClick={scrollToTop} className="rounded-full border-primary/20 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 ml-4" aria-label="Back to top">
              <ArrowUp className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>;
}